import { useMemo } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import { useFlightPlansQuery } from '@/api/v1/flight-plan-api';
import { FlightPlanRequest } from '@/api/v1/flight-plan-types';

export function FlightPlanLayer() {
  const { data: flightPlans, isLoading, error } = useFlightPlansQuery();

  if (isLoading || error || !flightPlans || flightPlans.length === 0) {
    return null;
  }

  return (
    <>
      {flightPlans.map(fp => (
        <FlightPlanItem 
          key={fp.id} 
          flightPlan={fp} 
        />
      ))}
    </>
  );
}

interface FlightPlanItemProps {
  flightPlan: FlightPlanRequest;
}

export function FlightPlanItem({ flightPlan }: FlightPlanItemProps) {
  const identifier = useMemo(() => flightPlan.id, [flightPlan.id]);
  
  const geoJsonData = useMemo(() => {
    const geoJson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { id: identifier },
          geometry: { type: 'Polygon', coordinates: flightPlan.polygon_zone }
        }
      ]
    };
    return geoJson;
  }, [identifier, flightPlan.polygon_zone]);

  const fillLayerStyle = {
    id: `flight-plan-fill-${identifier}`,
    type: 'fill' as const,
    paint: {
      'fill-color': '#0080FF',
      'fill-opacity': 0.15
    }
  };

  const outlineLayerStyle = {
    id: `flight-plan-outline-${identifier}`,
    type: 'line' as const,
    paint: {
      'line-color': '#0080FF',
      'line-width': 2
    }
  };

  return (
    <Source 
      id={`flight-plan-source-${identifier}`} 
      type="geojson" 
      data={geoJsonData as unknown as GeoJSON.FeatureCollection<GeoJSON.Geometry>}
    >
      <Layer {...fillLayerStyle} />
      <Layer {...outlineLayerStyle} />
    </Source>
  );
}
